import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { useUser } from 'context/user-context';
import { generateId } from 'utils/id';
import { GET_RETURN_EXCHANGE_CUSTOMIZATION_REVIEW } from 'gql/queries';
import { ACKNOWLEDGE_RETURN_EXCHANGE_CUSTOMIZATION } from 'gql/mutations';
import {
  customizationInstructionKey,
  parseReturnCustomizationReview,
  type ReturnCustomizationReview,
} from './ReturnCustomizationReview.contract';
interface State {
  scope: object;
  source: ReturnCustomizationReview | null;
  pending: boolean;
  needsReload: boolean;
  error: string | null;
}
const ERROR =
  'Replacement instructions could not be verified. Check the latest instructions before trying again.';
const uuid = (value: unknown): value is string =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );
export default function useReturnCustomizationReview(
  storeId: string,
  returnId: string,
) {
  const { user } = useUser(),
    actorId = user?.id,
    key = JSON.stringify([storeId, returnId, actorId]),
    owner = useRef({ key }),
    mounted = useRef(true),
    operation = useRef<object | null>(null);
  const attempt = useRef<{
    scope: object;
    intentId: string;
    digest: string;
    requestId: string;
    revision: number;
    instructions: string;
  } | null>(null);
  if (owner.current.key !== key) {
    owner.current = { key };
    operation.current = null;
    attempt.current = null;
  }
  const scope = owner.current,
    initial: State = {
      scope,
      source: null,
      pending: false,
      needsReload: false,
      error: null,
    };
  const [state, setState] = useState(initial),
    current = state.scope === scope ? state : initial,
    latest = useRef(current);
  latest.current = current;
  const read = useQuery(GET_RETURN_EXCHANGE_CUSTOMIZATION_REVIEW, {
      variables: { input: { storeId, returnId } },
      skip: !uuid(storeId) || !uuid(returnId),
      fetchPolicy: 'network-only',
    }),
    [write] = useMutation(ACKNOWLEDGE_RETURN_EXCHANGE_CUSTOMIZATION);
  const readState = useRef({ loading: read.loading, error: read.error });
  readState.current = { loading: read.loading, error: read.error };
  const live = () => mounted.current && owner.current === scope;
  const update = (next: State) => {
    if (live()) {
      latest.current = next;
      setState(next);
    }
  };
  const compatible = (
    next: ReturnCustomizationReview,
    previous: ReturnCustomizationReview | null,
  ) =>
    !previous ||
    previous.intentId !== next.intentId ||
    !previous.intentId ||
    ((next.revision || 0) >= (previous.revision || 0) &&
      customizationInstructionKey(next) ===
        customizationInstructionKey(previous) &&
      (!previous.acknowledgment ||
        JSON.stringify(next.acknowledgment) ===
          JSON.stringify(previous.acknowledgment)));
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  const raw = read.data?.returnExchangeCustomizationReview;
  useEffect(() => {
    if (
      read.loading ||
      read.error ||
      operation.current ||
      latest.current.needsReload
    )
      return;
    const source = parseReturnCustomizationReview(raw, storeId, returnId);
    if (source && compatible(source, latest.current.source))
      update({ ...initial, source });
    // Only a verified fresh read can update this store/return/actor generation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, raw, read.loading, read.error]);
  async function acknowledge(input: {
    intentId: string;
    expectedDigest: string;
  }): Promise<boolean> {
    const before = latest.current.source;
    if (
      !live() ||
      operation.current ||
      latest.current.needsReload ||
      readState.current.loading ||
      readState.current.error ||
      !uuid(actorId) ||
      !before?.canAcknowledge ||
      before.status !== 'AWAITING_ACKNOWLEDGMENT' ||
      before.intentId !== input.intentId ||
      before.intentDigest !== input.expectedDigest ||
      !before.revision
    )
      return false;
    const instructions = customizationInstructionKey(before);
    if (
      !attempt.current ||
      attempt.current.scope !== scope ||
      attempt.current.intentId !== input.intentId ||
      attempt.current.digest !== input.expectedDigest ||
      attempt.current.instructions !== instructions
    ) {
      const requestId = generateId();
      if (!uuid(requestId)) return false;
      attempt.current = {
        scope,
        intentId: input.intentId,
        digest: input.expectedDigest,
        requestId,
        revision: before.revision,
        instructions,
      };
    }
    const request = attempt.current,
      ticket = {};
    operation.current = ticket;
    update({ ...latest.current, pending: true, error: null });
    const owns = () => live() && operation.current === ticket;
    try {
      const response = await write({
        variables: {
          input: { storeId, returnId, ...input, requestId: request.requestId },
        },
      });
      if (!owns()) return false;
      const source = parseReturnCustomizationReview(
          response.data?.acknowledgeReturnExchangeCustomization,
          storeId,
          returnId,
        ),
        ack = source?.acknowledgment;
      if (
        response.errors?.length ||
        !source ||
        !ack ||
        source.intentId !== request.intentId ||
        source.intentDigest !== request.digest ||
        customizationInstructionKey(source) !== request.instructions ||
        ack.requestId !== request.requestId ||
        ack.actorId !== actorId ||
        ack.revision <= request.revision ||
        source.canAcknowledge ||
        !['ACKNOWLEDGED', 'PROCESSING', 'READY'].includes(source.status)
      )
        throw new Error(ERROR);
      update({
        scope,
        source,
        pending: false,
        needsReload: false,
        error: null,
      });
      return true;
    } catch {
      if (owns())
        update({
          ...latest.current,
          pending: false,
          needsReload: true,
          error: ERROR,
        });
      return false;
    } finally {
      if (owns()) operation.current = null;
    }
  }
  async function reload(): Promise<boolean> {
    if (!live() || operation.current) return false;
    const ticket = {};
    operation.current = ticket;
    update({ ...latest.current, pending: true });
    const owns = () => live() && operation.current === ticket;
    try {
      const response = await read.refetch({ input: { storeId, returnId } });
      if (!owns()) return false;
      const source = parseReturnCustomizationReview(
        response.data?.returnExchangeCustomizationReview,
        storeId,
        returnId,
      );
      if (
        response.errors?.length ||
        !source ||
        !compatible(source, latest.current.source)
      )
        throw new Error(ERROR);
      update({
        scope,
        source,
        pending: false,
        needsReload: false,
        error: null,
      });
      return true;
    } catch {
      if (owns())
        update({
          ...latest.current,
          pending: false,
          needsReload: true,
          error: ERROR,
        });
      return false;
    } finally {
      if (owns()) operation.current = null;
    }
  }
  return {
    ...current,
    loading: read.loading,
    readError: read.error,
    canAcknowledge:
      !current.pending &&
      !current.needsReload &&
      !read.loading &&
      !read.error &&
      uuid(actorId) &&
      current.source?.canAcknowledge === true,
    acknowledge,
    reload,
  };
}
